import Konva from 'konva';
import { markRaw } from 'vue';
import { defaultCorners, getHomography, validCorners } from '@/utils/perspective';
import { normalizeEraser, hasEraser, capsulePolygon, inverseHomographyPoint, applyErasePaths, applyRepairPaths, mapEraserPaths, strokesHavePaths } from '@/utils/objectEraser';

export default {
  props: {
    eraserEnabled: { type: Boolean, default: false },
    eraserSize: { type: Number, default: 40 },
    eraserMode: { type: String, default: 'erase' }
  },
  data() {
    return { eraseDraft: null, eraseCursor: null, eraserCommitting: false, eraseDrawFrame: null };
  },
  computed: {
    eraseCursorStyle() {
      return { left: `${this.eraseCursor.x - this.eraserSize / 2}px`,
        top: `${this.eraseCursor.y - this.eraserSize / 2}px`,
        width: `${this.eraserSize}px`, height: `${this.eraserSize}px` };
    },
    erasableSelectedItem() {
      const item = this.selectedTransformItem;
      return item && !item.locked && !item.isBackground
        && (this.selectedGroupId || !item.groupId) ? item : null;
    }
  },
  watch: {
    eraserMode() { this.cancelEraseStroke(); },
    eraserEnabled() {
      this.cancelEraseStroke();
      this.eraseCursor = null;
      this.$nextTick(() => this.updateTransformer());
    },
    selectedId() { this.cancelEraseStroke(); },
    selectedGroupId() { this.cancelEraseStroke(); },
    viewportKey() { this.cancelEraseStroke(); },
    stageWidth() { this.cancelEraseStroke(); },
    stageHeight() { this.cancelEraseStroke(); },
    'selectedTransformItem.eraser'() {
      if (!this.eraserCommitting) this.cancelEraseStroke();
    },
    'selectedTransformItem.locked'(locked) {
      if (locked) { this.cancelEraseStroke(); this.$emit('exit-eraser'); }
    }
  },
  beforeUnmount() { this.cancelEraseStroke(); },
  methods: {
    itemHasErasure(item) {
      // Rendering only needs to choose raster vs native node. Validate and
      // copy full masks at save/source-build time, not on every cursor move.
      const mask = item?.eraser;
      return mask?.version === 1 && Array.isArray(mask.strokes)
        && strokesHavePaths(mask.strokes);
    },
    isEraseDraft(canvasItem) {
      return Boolean(this.eraseDraft && String(canvasItem.node.id) === String(this.eraseDraft.id)
        && (canvasItem.type === 'group') === this.eraseDraft.group);
    },
    displayEraser(item, type) {
      const mask = normalizeEraser(item?.eraser);
      if (!mask || type !== 'image' || !item.flipX) return mask;
      return mapEraserPaths(mask, path => {
        for (let i = 0; i < path.length; i += 2) path[i] = 1 - path[i];
        return path;
      });
    },
    updateEraseCursor(event) {
      const rect = this.$refs.editorContainer.getBoundingClientRect();
      this.eraseCursor = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    },
    hideEraseCursor() { if (!this.eraseDraft) this.eraseCursor = null; },
    handleEditorPointerDown(event) {
      this.handleViewportPointerDown(event);
      if (!this.eraserEnabled || this.exportMode || this.pointerPan || this.spacePan || this.panMode) return;
      if (event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      if (this.navigationActive || this.eraseDraft || this.eraserCommitting || !this.erasableSelectedItem) return;
      const node = this.getSelectedKonvaNode();
      const item = this.erasableSelectedItem;
      if (this.eraserMode === 'restore' && !hasEraser(item.eraser)) return;
      const canvasItem = this.sortedKonvaItems.find(entry =>
        String(entry.node.id) === String(item.id) && (entry.type === 'group') === Boolean(this.selectedGroupId));
      if (!node || !canvasItem || !node.width() || !node.height()) return;
      const source = this.getWarpSource(canvasItem);
      if (!source) return;
      const original = this.eraserMode === 'restore' ? this.getWarpSource(canvasItem, 1, true) : null;
      if (this.eraserMode === 'restore' && !original) return;
      if (node.getClassName() === 'Shape') node.clearCache();
      const preview = document.createElement('canvas');
      preview.width = source.canvas.width;
      preview.height = source.canvas.height;
      preview.getContext('2d').drawImage(source.canvas, 0, 0);
      const point = this.viewportEventPoint(event);
      const rect = this.$refs.editorContainer.getBoundingClientRect();
      this.eraseDraft = markRaw({
        id: item.id, group: Boolean(this.selectedGroupId), pointerId: event.pointerId,
        item, type: canvasItem.type, paths: [], last: point,
        mode: this.eraserMode === 'restore' ? 'restore' : 'erase', original: original?.canvas,
        inverse: node.getAbsoluteTransform().copy().invert(),
        originalGroup: node.getClassName() === 'Group',
        width: node.width(), height: node.height(),
        homography: getHomography(validCorners(item.perspective) ? item.perspective : defaultCorners()),
        radius: Math.max(0.5, Math.min(40, this.eraserSize / 2)) * this.stageWidth / rect.width,
        source: { canvas: preview, pixelRatio: source.pixelRatio || 1, sourceRect: { ...source.sourceRect } }
      });
      this.$refs.editorContainer.setPointerCapture(event.pointerId);
      this.$refs.editorContainer.focus({ preventScroll: true });
      this.updateEraseCursor(event);
      this.appendEraseSegment(point);
      this.$nextTick(() => this.scheduleEraseDraw());
    },
    eraseSourcePoint(screen) {
      const draft = this.eraseDraft;
      if (!draft) return null;
      const local = draft.inverse.point(screen);
      const point = inverseHomographyPoint(draft.homography,
        local.x / draft.width + (draft.originalGroup ? 0.5 : 0),
        local.y / draft.height + (draft.originalGroup ? 0.5 : 0));
      return point && { x: Math.round(point.x * 1e6) / 1e6, y: Math.round(point.y * 1e6) / 1e6 };
    },
    appendEraseSegment(point) {
      const draft = this.eraseDraft;
      if (!draft) return;
      const path = capsulePolygon(draft.last, point, draft.radius, p => this.eraseSourcePoint(p));
      draft.last = point;
      if (!path) return;
      const { sourceRect, canvas } = draft.source;
      // A brush outside the selected object's raster must not create a history
      // entry or affect the background/other objects beneath the pointer.
      const xs = path.filter((_, i) => i % 2 === 0), ys = path.filter((_, i) => i % 2 === 1);
      if (Math.max(...xs) < -sourceRect.x / sourceRect.width
        || Math.min(...xs) > (canvas.width - sourceRect.x) / sourceRect.width
        || Math.max(...ys) < -sourceRect.y / sourceRect.height
        || Math.min(...ys) > (canvas.height - sourceRect.y) / sourceRect.height) return;
      if (draft.mode === 'restore') applyRepairPaths(canvas.getContext('2d'), [path], sourceRect, draft.original);
      else applyErasePaths(canvas.getContext('2d'), [path], sourceRect);
      // Image mirroring is baked into the source bitmap. Persist unmirrored
      // mask coordinates so later mirror operations mirror the holes as well.
      const stored = path.slice();
      if (draft.type === 'image' && draft.item.flipX) {
        for (let i = 0; i < stored.length; i += 2) stored[i] = 1 - stored[i];
      }
      draft.paths.push(stored);
      this.scheduleEraseDraw();
    },
    scheduleEraseDraw() {
      if (this.eraseDrawFrame != null) return;
      this.eraseDrawFrame = requestAnimationFrame(() => {
        this.eraseDrawFrame = null;
        this.$refs.layer?.getNode()?.draw();
      });
    },
    handleEditorPointerMove(event) {
      if (this.pointerPan) { this.handleViewportPointerMove(event); return; }
      if (!this.eraserEnabled || this.exportMode || this.navigationActive || this.spacePan) return;
      if (this.eraseDraft && this.eraseDraft.pointerId !== event.pointerId) return;
      this.updateEraseCursor(event);
      if (!this.eraseDraft || this.eraserCommitting) return;
      event.preventDefault();
      event.stopPropagation();
      const point = this.viewportEventPoint(event);
      if (Math.hypot(point.x - this.eraseDraft.last.x, point.y - this.eraseDraft.last.y) < 1) return;
      this.appendEraseSegment(point);
    },
    async handleEditorPointerUp(event) {
      this.handleViewportPointerUp(event);
      const draft = this.eraseDraft;
      if (!draft || draft.pointerId !== event.pointerId || this.eraserCommitting) return;
      event.preventDefault();
      event.stopPropagation();
      if (this.navigationActive || !this.erasableSelectedItem) { this.cancelEraseStroke(); return; }
      this.appendEraseSegment(this.viewportEventPoint(event));
      if (!draft.paths.length) { this.cancelEraseStroke(); return; }
      const prior = normalizeEraser(draft.item.eraser) || { version: 1, strokes: [] };
      const stroke = { paths: draft.paths.map(path => path.slice()) };
      if (draft.mode === 'restore') stroke.mode = 'restore';
      const mask = { version: 1, strokes: [...prior.strokes, stroke] };
      this.eraserCommitting = true;
      this.releaseErasePointer(draft.pointerId);
      try {
        // One committed stroke emits one normal model update, hence one undo.
        this.$emit(draft.group ? 'update:groups' : 'update:elements',
          (draft.group ? this.groups : this.elements).map(item =>
            String(item.id) === String(draft.id) ? { ...item, eraser: mask } : item));
        await this.$nextTick();
        await this.loadImages();
        this.loadTexts();
        await this.loadGroups();
      } finally {
        this.eraseDraft = null;
        this.eraserCommitting = false;
        if (event.pointerType === 'touch') this.eraseCursor = null;
        this.$nextTick(() => this.$refs.layer?.getNode()?.draw());
      }
    },
    releaseErasePointer(id) {
      const container = this.$refs.editorContainer;
      if (container?.hasPointerCapture(id)) container.releasePointerCapture(id);
    },
    cancelErasePointer(event) {
      this.handleViewportPointerUp(event);
      if (this.eraseDraft?.pointerId === event.pointerId) this.cancelEraseStroke();
    },
    cancelEraseStroke() {
      const hadDraft = Boolean(this.eraseDraft);
      if (this.eraseDraft) this.releaseErasePointer(this.eraseDraft.pointerId);
      this.eraseDraft = null;
      if (this.eraseDrawFrame != null) cancelAnimationFrame(this.eraseDrawFrame);
      this.eraseDrawFrame = null;
      this.eraseCursor = null;
      this.$nextTick(() => {
        if (hadDraft) {
          this.cacheFilteredImages();
          this.cacheGroupFilteredImages();
        }
        this.$refs.layer?.getNode()?.batchDraw();
      });
    },
    resetEraser() {
      const item = this.erasableSelectedItem;
      if (!item || !hasEraser(item.eraser)) return;
      this.cancelEraseStroke();
      const group = Boolean(this.selectedGroupId);
      this.$emit(group ? 'update:groups' : 'update:elements',
        (group ? this.groups : this.elements).map(entry => {
          if (String(entry.id) !== String(item.id)) return entry;
          const restored = { ...entry };
          delete restored.eraser;
          return restored;
        }));
    },
    getUngroupErasure(groupId, itemId) {
      const group = this.groupNodes.find(entry => String(entry.id) === String(groupId));
      const child = group && [...group.imageChildren, ...group.textChildren]
        .find(entry => String(entry.id) === String(itemId));
      const item = child?.originalItem || this.elements.find(entry => String(entry.id) === String(itemId));
      const prior = normalizeEraser(item?.eraser);
      const mask = normalizeEraser(group?.originalGroup.eraser);
      if (!child || !mask || !hasEraser(mask)) return prior;
      const config = child.config;
      const inverse = new Konva.Transform()
        .translate(config.x, config.y).rotate((config.rotation || 0) * Math.PI / 180)
        .scale(config.scaleX || 1, config.scaleY || 1)
        .translate(-(config.offsetX || 0), -(config.offsetY || 0)).invert();
      const h = getHomography(validCorners(item.perspective) ? item.perspective : defaultCorners());
      const projectedMask = mapEraserPaths(mask, path => {
          const projected = [];
          for (let i = 0; i < path.length; i += 2) {
            const local = inverse.point({ x: path[i] * group.config.width - group.config.width / 2,
              y: path[i + 1] * group.config.height - group.config.height / 2 });
            const point = inverseHomographyPoint(h, local.x / config.width, local.y / config.height);
            if (!point) { projected.length = 0; break; }
            projected.push(item.type !== 'text' && item.flipX ? 1 - point.x : point.x, point.y);
          }
          return projected.length ? projected : null;
      });
      // A group repair must not undo a child's pre-existing erasure when the
      // group mask is transferred. Merge the completed group coverage as an
      // independent layer; later repair on the child can restore either layer.
      const strokes = projectedMask.strokes.some(stroke => stroke.mode === 'restore' || stroke.layer)
        ? [{ paths: [], layer: projectedMask.strokes }] : projectedMask.strokes;
      return normalizeEraser({ version: 1, strokes: [...(prior?.strokes || []), ...strokes] });
    }
  }
};